import React, { useEffect } from 'react';
import clsx from 'clsx';
import { useEditor } from '@craftjs/core';
import { TextField as InspectorTextField } from '@/components/inspector-fields/TextField/TextField';
import { useFormFieldHiddenSync } from '@/hooks/useFormFieldHiddenSync';
import { TextAreaField as InspectorTextAreaField } from '@/components/inspector-fields/TextAreaField/TextAreaField';
import IconClose from '@/assets/icons/icon-close.svg?react';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import { Switch } from '@/components/inspector-fields/Switch/Switch';
import { SelectField } from '@/components/dropdowns/SelectField/SelectField';
import { SelectItem } from '@/components/ui/select';
import type { FormConsentFieldProps as SharedFormConsentFieldProps } from '@builder4/shared/components/Form';
import type { LinkVariant } from '@builder4/shared/types/styles';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { FORM_CONSENT_FIELD_NODE_NAME, FIELD_LABEL_CONSENT } from '@/utils/constants';

interface ConsentFieldProps {
  className?: string;
  title?: string;
  onClose?: () => void;
  /** The specific field node ID to edit */
  fieldNodeId: string;
  getDefaultFieldLabel: () => string;
}

export const ConsentField: React.FC<ConsentFieldProps> = ({
  className = '',
  title = 'Consent Field',
  onClose,
  fieldNodeId,
  getDefaultFieldLabel,
}) => {
  const { actions, fieldProps } = useEditor((_, query) => {
    const node = query.node(fieldNodeId).get();
    return {
      fieldProps: (node?.data.props || {}) as SharedFormConsentFieldProps,
    };
  });

  const { setBPProp, getBPProp } = useBreakpointAwareProp(FORM_CONSENT_FIELD_NODE_NAME);

  const label = getBPProp(fieldProps, 'label');
  const text = getBPProp(fieldProps, 'text');
  const showDescription = getBPProp(fieldProps, 'showDescription');
  const description = getBPProp(fieldProps, 'description');
  const showUrlLink = getBPProp(fieldProps, 'showUrlLink');
  const linkUrl = getBPProp(fieldProps, 'linkUrl');
  const linkText = getBPProp(fieldProps, 'linkText');
  const selectedLinkVariant = getBPProp(fieldProps, 'selectedLinkVariant');
  const required = getBPProp(fieldProps, 'required');
  const selectedByDefault = getBPProp(fieldProps, 'selectedByDefault');
  const hidden = getBPProp(fieldProps, 'hidden');

  // Throttled — batches rapid keystrokes (text inputs)
  const setProp = (callback: (props: SharedFormConsentFieldProps) => void) => {
    actions.history.throttle(500).setProp(fieldNodeId, callback);
  };

  // Direct — discrete actions (toggles, selects, onBlur)
  const setPropDirect = (callback: (props: SharedFormConsentFieldProps) => void) => {
    actions.setProp(fieldNodeId, callback);
  };

  // Sync hidden prop with layer visibility
  const { handleHiddenChange } = useFormFieldHiddenSync<SharedFormConsentFieldProps>(
    fieldNodeId,
    hidden,
    setPropDirect,
  );

  useEffect(() => {
    actions.history.ignore().setCustom(fieldNodeId, (custom) => {
      custom.displayName = label || FIELD_LABEL_CONSENT;
    });
  }, [label, actions, fieldNodeId]);

  return (
    <div
      className={clsx(
        'bg-neutral-0 inline-flex h-[480px] max-h-[480px] w-[248px] flex-col items-center overflow-hidden rounded-lg shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)] outline-[0.8px] outline-neutral-100',
        className,
      )}
      data-craft-history
    >
      {/* Card header */}
      <div className="bg-neutral-0 inline-flex h-10 w-full items-center justify-between rounded-t-lg border border-neutral-100 px-3">
        <div className="text-xs leading-5 font-semibold text-neutral-900">{title}</div>
        <IconSimpleButton aria-label="Close" icon={IconClose} onClick={onClose} />
      </div>

      <div className="flex min-h-0 w-full flex-1 flex-col border border-t-0 border-neutral-100">
        <div className="no-scrollbar flex-1 overflow-y-auto">
          <div className="flex w-full flex-col items-start justify-start gap-3 px-3 pt-3 pb-4">
            {/* Field Title - syncs with Layers panel */}
            <InspectorTextField
              className="w-full"
              classNameInputParent="max-w-none"
              classNameInput="max-w-[140px]"
              text="Field Label"
              inputValue={label}
              placeholderText="Consent"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setProp((props: SharedFormConsentFieldProps) => {
                  setBPProp(props, 'label', e.target.value);
                });
              }}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                if (!e.target.value) {
                  setProp((props: SharedFormConsentFieldProps) => {
                    setBPProp(props, 'label', getDefaultFieldLabel());
                  });
                }
              }}
            />

            {/* Text */}
            <div className="flex w-full flex-col gap-2">
              <div className="text-button-sm leading-4 font-medium text-neutral-500">Text</div>
              <InspectorTextField
                className="w-full"
                classNameInputParent="w-full max-w-full"
                classNameInput="w-full max-w-full"
                text=""
                inputValue={text}
                placeholderText="Yes, I consent to receiving emails"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setProp((props: SharedFormConsentFieldProps) => {
                    setBPProp(props, 'text', e.target.value);
                  })
                }
              />
            </div>

            {/* Description toggle + content */}
            <Switch
              label="Description"
              checked={showDescription}
              onCheckedChange={(checked: boolean) =>
                setPropDirect((props: SharedFormConsentFieldProps) => {
                  setBPProp(props, 'showDescription', checked);
                })
              }
            >
              <InspectorTextAreaField
                className="w-full"
                inputValue={description}
                placeholderText="This form collects information we will use to send you updates about promotions, special offers, and news. We will not share or sell your personal information. You can unsubscribe at any time."
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setProp((props: SharedFormConsentFieldProps) => {
                    setBPProp(props, 'description', e.target.value);
                  })
                }
              />
            </Switch>

            {/* Divider */}
            <div className="self-stretch pb-1">
              <div className="h-0 border-t border-neutral-100" />
            </div>

            {/* Include URL link */}
            <Switch
              label="Include a URL link"
              checked={showUrlLink}
              onCheckedChange={(checked: boolean) =>
                setPropDirect((props: SharedFormConsentFieldProps) => {
                  setBPProp(props, 'showUrlLink', checked);
                })
              }
            >
              <div className="flex w-full flex-col gap-2">
                <InspectorTextField
                  className="w-full"
                  classNameInputParent="max-w-none"
                  classNameInput="max-w-[160px]"
                  text="Link Text"
                  inputValue={linkText}
                  placeholderText="Privacy Policy"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setProp((props: SharedFormConsentFieldProps) => {
                      setBPProp(props, 'linkText', e.target.value);
                    })
                  }
                />
                <InspectorTextField
                  className="w-full"
                  classNameInputParent="max-w-none"
                  classNameInput="max-w-[160px]"
                  text="URL"
                  inputValue={linkUrl}
                  placeholderText="www.leadpages.com"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setProp((props: SharedFormConsentFieldProps) => {
                      setBPProp(props, 'linkUrl', e.target.value);
                    })
                  }
                />
                <SelectField
                  text="Link Style"
                  className="w-full"
                  classNameTrigger="w-[160px]"
                  defaultValue={selectedLinkVariant || 'link1'}
                  onValueChange={(value) =>
                    setPropDirect((props: SharedFormConsentFieldProps) => {
                      setBPProp(props, 'selectedLinkVariant', value as LinkVariant);
                    })
                  }
                >
                  <SelectItem value="link1">Link Style 1</SelectItem>
                  <SelectItem value="link2">Link Style 2</SelectItem>
                </SelectField>
              </div>
            </Switch>

            {/* Other toggles */}
            <div className="flex w-full flex-col">
              <Switch
                label="This field is required"
                checked={required}
                onCheckedChange={(checked: boolean) =>
                  setPropDirect((props: SharedFormConsentFieldProps) => {
                    setBPProp(props, 'required', checked);
                  })
                }
              />
              <Switch
                label="Selected by default"
                checked={selectedByDefault}
                onCheckedChange={(checked: boolean) =>
                  setPropDirect((props: SharedFormConsentFieldProps) => {
                    setBPProp(props, 'selectedByDefault', checked);
                  })
                }
              />
              <Switch
                label="This is a hidden field"
                checked={hidden}
                onCheckedChange={handleHiddenChange}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsentField;
